/**
 * Wegstein tezlatish — x = g(x) ko'rinishidagi qo'zg'almas nuqta iteratsiyasi uchun
 * (flowsheet retsikl/tear stream'lari).
 *
 * TENGLAMALAR (har o'zgaruvchi alohida, komponentli Wegstein)
 *   s_k = (g_k − g_{k−1}) / (x_k − x_{k−1})       sekant qiyaligi
 *   q_k = s_k / (s_k − 1)
 *   x_{k+1} = q_k · x_k + (1 − q_k) · g_k
 *   q = 0  → oddiy ketma-ket yaqinlashish;  q < 0 → ekstrapolyatsiya (tezlatish);
 *   0 < q < 1 → damping. q chegaralanadi [qMin, qMax] (odatda [−5, 0]) —
 *   chegarasiz Wegstein tebranishni kuchaytirishi mumkin.
 *
 * MANBA: Wegstein (1958); Seider, Seader, Lewin — "Product and Process Design
 * Principles", 2-nashr, 7-bob (sequential modular, tear streams).
 */

export interface WegsteinOptions {
  qMin?: number;
  qMax?: number;
  /** Shu iteratsiyadan boshlab tezlatish (oldinroq — oddiy almashtirish) */
  startAt?: number;
}

export interface WegsteinState {
  k: number;
  xPrev: number[] | null;
  gPrev: number[] | null;
}

export function wegsteinInit(): WegsteinState {
  return { k: 0, xPrev: null, gPrev: null };
}

/**
 * Bitta qadam: x_k va g_k = g(x_k) berilgan, keyingi x_{k+1} qaytariladi.
 * Holat (oldingi x, g) chaqiruvchi tomonidan saqlanadi.
 */
export function wegsteinStep(state: WegsteinState, x: number[], g: number[], opts: WegsteinOptions = {}): number[] {
  const qMin = opts.qMin ?? -5;
  const qMax = opts.qMax ?? 0;
  const startAt = opts.startAt ?? 2;
  const next = new Array<number>(x.length);
  const useAccel = state.k + 1 >= startAt && state.xPrev && state.gPrev;
  for (let i = 0; i < x.length; i++) {
    const xi = x[i] as number;
    const gi = g[i] as number;
    let q = 0;
    if (useAccel) {
      const dx = xi - (state.xPrev as number[])[i]!;
      const dg = gi - (state.gPrev as number[])[i]!;
      if (Math.abs(dx) > 1e-14 * Math.max(1, Math.abs(xi))) {
        const s = dg / dx;
        if (Math.abs(s - 1) > 1e-12) q = s / (s - 1);
        if (!Number.isFinite(q)) q = 0;
        q = Math.min(qMax, Math.max(qMin, q));
      }
    }
    next[i] = q * xi + (1 - q) * gi;
  }
  state.xPrev = x.slice();
  state.gPrev = g.slice();
  state.k++;
  return next;
}
